import { FC, useState, useEffect, useCallback } from "react"
import { MessageCircle, Loader2, RotateCcw } from "lucide-react"
import { Brand } from "../../../../types"
import BrandAutocomplete from "../../brand-autocomplete"
import { PrimaryBtn } from "../../buttons"
import brandSmsSettingsApi, {
  BrandSmsSettings as BrandSmsSettingsValues
} from "../../../../api/brand-sms-settings"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import ConfigToolCard from "./config-tool-card"

interface Props {
  brands: Brand[]
}

const DEFAULT_AGENT_NAME = "Katie"

const isSuccess = (status?: string) => status === "success"

const BrandSmsSettings: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [loaded, setLoaded] = useState<BrandSmsSettingsValues | null>(null)
  const [agentName, setAgentName] = useState<string>("")
  const [sendInitialSummary, setSendInitialSummary] = useState<boolean>(true)
  const [sendDashboardLink, setSendDashboardLink] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const applySettings = (settings: BrandSmsSettingsValues) => {
    setLoaded(settings)
    setAgentName(settings.agent_name ?? "")
    setSendInitialSummary(!!settings.send_initial_summary)
    setSendDashboardLink(!!settings.send_dashboard_link)
  }

  const fetchSettings = useCallback(
    async (brandId: string) => {
      if (!brandId) return
      setIsLoading(true)
      try {
        const res = await brandSmsSettingsApi.get(brandId)
        if (isSuccess(res.status) && res.settings) {
          applySettings(res.settings)
        } else {
          dispatch(
            pushToast({
              msg: res.message || "Failed to load settings",
              theme: "danger",
              timeout: 5000
            })
          )
          setLoaded(null)
        }
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load settings"
        dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
        setLoaded(null)
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (selectedBrandId) {
      fetchSettings(selectedBrandId)
    } else {
      setLoaded(null)
      setAgentName("")
      setSendInitialSummary(true)
      setSendDashboardLink(true)
    }
  }, [selectedBrandId, fetchSettings])

  const trimmedAgentName = agentName.trim()
  const isDirty =
    !!loaded &&
    (trimmedAgentName !== (loaded.agent_name ?? "").trim() ||
      sendInitialSummary !== loaded.send_initial_summary ||
      sendDashboardLink !== loaded.send_dashboard_link)

  const handleSave = async () => {
    if (!selectedBrandId || !loaded || !isDirty) return
    setIsSaving(true)
    try {
      const payload: Record<string, any> = {
        brand_id: parseInt(selectedBrandId, 10)
      }
      if (trimmedAgentName !== (loaded.agent_name ?? "").trim()) {
        payload.agent_name = trimmedAgentName
      }
      if (sendInitialSummary !== loaded.send_initial_summary) {
        payload.send_initial_summary = sendInitialSummary
      }
      if (sendDashboardLink !== loaded.send_dashboard_link) {
        payload.send_dashboard_link = sendDashboardLink
      }
      const res = await brandSmsSettingsApi.update(payload as any)
      if (isSuccess(res.status) && res.settings) {
        applySettings(res.settings)
        dispatch(
          pushToast({
            msg: res.message || "Settings saved",
            theme: "success",
            timeout: 3000
          })
        )
      } else {
        dispatch(
          pushToast({
            msg: res.message || "Failed to save settings",
            theme: "danger",
            timeout: 5000
          })
        )
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save settings"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetAgentName = () => {
    setAgentName("")
  }

  const usingDefaultName =
    !!loaded && (loaded.agent_name ?? "") === DEFAULT_AGENT_NAME

  return (
    <ConfigToolCard
      icon={<MessageCircle className="w-5 h-5 text-primary-600" />}
      iconClassName="bg-primary-100"
      title="SMS Followup Settings"
      description="Per-brand persona name and toggles for what the AI agent includes in followup texts"
    >
      <div className="p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Brand:
          </label>
          <BrandAutocomplete
            selected={selectedBrandId}
            brands={brands}
            direction="vertical"
            hideLabel
            onChange={setSelectedBrandId}
          />
        </div>

        {!selectedBrandId ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <MessageCircle className="w-8 h-8 mb-2" />
            <p className="text-sm">Select a brand to manage settings</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : loaded ? (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent Name
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={agentName}
                  onChange={e => setAgentName(e.target.value)}
                  placeholder={DEFAULT_AGENT_NAME}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {trimmedAgentName !== "" && (
                  <button
                    onClick={handleResetAgentName}
                    type="button"
                    className="inline-flex items-center gap-1 px-2.5 py-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                    title="Clear override (falls back to default Katie)"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reset
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-400">
                The persona name the SMS bot uses. Leave blank to fall back to
                the default ({DEFAULT_AGENT_NAME}).
                {usingDefaultName &&
                  trimmedAgentName === DEFAULT_AGENT_NAME && (
                    <span className="ml-1 text-gray-500">
                      (Currently using default.)
                    </span>
                  )}
              </p>
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendInitialSummary}
                  onChange={e => setSendInitialSummary(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium">
                    Include initial rate summary
                  </span>
                  <span className="block text-xs text-gray-500">
                    Prepend the lead's rate summary to the followup SMS.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendDashboardLink}
                  onChange={e => setSendDashboardLink(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium">
                    Include customer dashboard link
                  </span>
                  <span className="block text-xs text-gray-500">
                    Append the personalized dashboard link to the followup SMS.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <PrimaryBtn
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                className="text-sm"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </PrimaryBtn>
              {isDirty && !isSaving && (
                <button
                  type="button"
                  onClick={() => loaded && applySettings(loaded)}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Discard changes
                </button>
              )}
              <p className="ml-auto text-xs text-gray-400">
                Only affects future SMS threads.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </ConfigToolCard>
  )
}

export default BrandSmsSettings
